import { useState, type HTMLAttributes } from 'react'
import type { CustomFieldDefinition } from '../../orders/api/intakeApi'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Switch } from '../../../components/ui/switch'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Textarea } from '../../../components/ui/textarea'
import { GripVertical, Trash2 } from 'lucide-react'

const FIELD_TYPES: { value: CustomFieldDefinition['type']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'multiselect', label: 'Multi-select' },
  { value: 'date', label: 'Date' },
  { value: 'file', label: 'File URL' },
  { value: 'url', label: 'URL' },
  { value: 'boolean', label: 'Boolean' },
]

export interface FieldEditorProps {
  field: CustomFieldDefinition
  index: number
  onChange: (updated: CustomFieldDefinition) => void
  onRemove: () => void
  /** dnd-kit sortable attributes + listeners, applied to the drag handle. */
  dragHandleProps?: HTMLAttributes<HTMLButtonElement>
}

export function FieldEditor({
  field,
  index,
  onChange,
  onRemove,
  dragHandleProps,
}: FieldEditorProps) {
  const [expanded, setExpanded] = useState(false)

  const update = (patch: Partial<CustomFieldDefinition>) => {
    onChange({ ...field, ...patch })
  }

  const updateValidation = (patch: Partial<NonNullable<CustomFieldDefinition['validation']>>) => {
    onChange({ ...field, validation: { ...field.validation, ...patch } })
  }

  const needsOptions = field.type === 'select' || field.type === 'multiselect'
  const optionsStr = (field.validation?.options ?? []).join('\n')

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Header row — always visible */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* Drag handle (pointer + keyboard via dnd-kit) */}
        <button
          type="button"
          {...dragHandleProps}
          aria-label={`Drag to reorder "${field.label || 'field'}"`}
          className="cursor-grab touch-none rounded p-0.5 text-muted-foreground shrink-0 hover:text-foreground active:cursor-grabbing focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Summary */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-2 min-w-0 text-left"
        >
          <span className="text-sm font-medium text-foreground truncate">
            {field.label || <span className="italic text-muted-foreground">Untitled field</span>}
          </span>
          <Badge variant="secondary" className="text-[10px] shrink-0">
            {FIELD_TYPES.find((t) => t.value === field.type)?.label ?? field.type}
          </Badge>
          {field.required && (
            <Badge variant="outline" className="text-[10px] shrink-0 border-amber-300 text-amber-700">
              Required
            </Badge>
          )}
        </button>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Remove field" className="text-muted-foreground hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="border-t border-border px-4 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor={`key-${index}`}>Key</Label>
              <Input
                id={`key-${index}`}
                value={field.key}
                onChange={(e) => update({ key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                placeholder="field_key"
              />
              <p className="text-[11px] text-muted-foreground">Lowercase identifier (letters, digits, underscore).</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`label-${index}`}>Label</Label>
              <Input
                id={`label-${index}`}
                value={field.label}
                onChange={(e) => update({ label: e.target.value })}
                placeholder="Display label"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor={`type-${index}`}>Type</Label>
              <select
                id={`type-${index}`}
                value={field.type}
                onChange={(e) => update({ type: e.target.value as CustomFieldDefinition['type'] })}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`help-${index}`}>Help text</Label>
              <Input
                id={`help-${index}`}
                value={field.helpText ?? ''}
                onChange={(e) => update({ helpText: e.target.value || undefined })}
                placeholder="Optional helper"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                id={`required-${index}`}
                checked={field.required ?? false}
                onCheckedChange={(v) => update({ required: v })}
              />
              <Label htmlFor={`required-${index}`}>Required</Label>
            </div>
          </div>

          {/* Validation */}
          {(field.type === 'number' || field.type === 'text' || field.type === 'textarea' || field.type === 'url') && (
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor={`min-${index}`}>Min {field.type === 'number' ? 'value' : 'length'}</Label>
                <Input
                  id={`min-${index}`}
                  type="number"
                  value={field.validation?.min ?? ''}
                  onChange={(e) => updateValidation({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`max-${index}`}>Max {field.type === 'number' ? 'value' : 'length'}</Label>
                <Input
                  id={`max-${index}`}
                  type="number"
                  value={field.validation?.max ?? ''}
                  onChange={(e) => updateValidation({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              </div>
              {(field.type === 'text' || field.type === 'textarea') && (
                <div className="space-y-1.5">
                  <Label htmlFor={`regex-${index}`}>Regex</Label>
                  <Input
                    id={`regex-${index}`}
                    value={field.validation?.regex ?? ''}
                    onChange={(e) => updateValidation({ regex: e.target.value || undefined })}
                    placeholder="^[A-Z].*"
                  />
                </div>
              )}
            </div>
          )}

          {/* Options for select/multiselect */}
          {needsOptions && (
            <div className="space-y-1.5">
              <Label htmlFor={`options-${index}`}>Options (one per line)</Label>
              <Textarea
                id={`options-${index}`}
                rows={4}
                value={optionsStr}
                onChange={(e) => {
                  const opts = e.target.value.split('\n').filter((o) => o.trim())
                  updateValidation({ options: opts })
                }}
                placeholder={"Option A\nOption B\nOption C"}
              />
            </div>
          )}

          {/* Default value */}
          <div className="space-y-1.5">
            <Label htmlFor={`default-${index}`}>Default value</Label>
            {field.type === 'boolean' ? (
              <Switch
                id={`default-${index}`}
                checked={Boolean(field.defaultValue)}
                onCheckedChange={(v) => update({ defaultValue: v })}
              />
            ) : (
              <Input
                id={`default-${index}`}
                value={String(field.defaultValue ?? '')}
                onChange={(e) => update({ defaultValue: e.target.value || undefined })}
                placeholder="Optional default"
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
